"use client";

import clsx from "clsx";
import { Form } from "../../../sanity/types";
import styles from "./FormBlock.module.scss";
import { InputField } from "../../form/fields/InputField/InputField";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useRef, useState } from "react";
import { acceptedFiles, siteAPIURL } from "../../../utilities/constants";
import { Fieldset } from "../../form/Fieldset/Fieldset";
import { RadioGroupField } from "../../form/fields/RadioGroupField/RadioGroupField";
import { CheckboxGroupField } from "../../form/fields/CheckboxGroupField/CheckboxGroupField";
import { Button } from "../../shared/Button/Button";
import { DropdownField } from "../../form/fields/DropdownField/DropdownField";
import { FileUploadField } from "../../form/fields/FileUploadField/FileUploadField";
import { DateField } from "@/components/form/fields/DateField/DateField";
import { PortableTextRichText } from "@/components/shared/PortableTextRichText/PortableTextRichText";
import { format, isWeekend } from "date-fns";
import { DateValue } from "react-aria-components";
import { SEND_PROPERTIES } from "@global/constants";

const { SEND_FROM, SEND_TO, SEND_TIME, SEND_SUBJECT } = SEND_PROPERTIES;

interface FormBlockProps {
	form?: Form | null;
}
const mapKeysToFieldInfo = (data?: FormBlockProps["form"]) => {
	if (!data?.fieldsets) return {};
	return data.fieldsets.reduce(
		(outerMap, fieldset) => {
			if (!fieldset.fields) return {};
			return fieldset.fields
				.filter(field => field._type === "field")
				.reduce((innerMap, field) => {
					const fullLabel = `${fieldset.heading}: ${field.label}`;
					innerMap[`${fieldset._key}:${field._key}`] = {
						label: fullLabel,
						type: field.fieldType,
					};
					return innerMap;
				}, outerMap);
		},
		{} as {
			[key: string]: {
				label: string;
				type: string | undefined;
			};
		},
	);
};

const requiredMessage = (isMandatory?: boolean) => (isMandatory ? "This field is required" : false);

export const FormBlock = ({ form }: FormBlockProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [sent, setSent] = useState<boolean>(false);

	const formMethods = useForm();
	const {
		control,
		handleSubmit,
		formState: { isSubmitting },
		setError,
		watch,
	} = formMethods;

	const fieldDatamap = useMemo(() => mapKeysToFieldInfo(form), [form]);

	if (!form) return null;

	const onSubmit = async (data: Record<string, any>) => {
		const mappedData: {
			[key: string]: {
				label: string;
				value: string;
				type: string | undefined;
			};
		} = {};

		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				for (const [fieldKey, fieldInfo] of Object.entries(fieldDatamap)) {
					if (key.startsWith(fieldKey)) {
						const baseKey = fieldKey;
						const label = fieldInfo.label;
						const value = data[key];

						// Create a unique identifier if it is a checkbox item
						const checkboxItem = key.substring(baseKey.length + 1);

						// Create a unique key for the mappedData object
						const mappedKey = checkboxItem.length > 0 ? `${baseKey} ${checkboxItem}` : baseKey;

						// Create a unique label for the mappedData object
						const uniqueLabel = checkboxItem.length > 0 ? `${label}: ${checkboxItem}` : label;

						// Add the entry to the mappedData object
						mappedData[mappedKey] = {
							label: uniqueLabel,
							type: fieldInfo.type,
							value: checkboxItem.length > 0 ? value : data[key],
						};
					}
				}
			}
		}

		// Unmapped data to it's original form
		const formData = new FormData();
		formData.append(SEND_FROM, form.sendingEmail || "");
		formData.append(SEND_TO, form.targetEmail || "");
		formData.append(
			SEND_TIME,
			new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }),
		);
		formData.append(SEND_SUBJECT, form.title || "Form submission");

		Object.values(mappedData).map(inputData => {
			if (
				Array.isArray(inputData.value) &&
				inputData.value.every((files: Blob) => acceptedFiles.includes(files.type))
			) {
				inputData.value.map((file: Blob) => {
					formData.append("attachments", file);
				});
				return;
			}
			if (inputData.type === "date" && inputData.value) {
				// TODO: This is bad, fix it up
				const value = inputData.value as unknown as DateValue;
				formData.append(inputData.label, format(value.toString(), "dd/MM/yyyy"));
				return;
			}

			formData.append(inputData.label, inputData.value ?? "");
		});

		const result = await postForm(siteAPIURL + "/email", formData);
		if (!result.success) {
			alert(result.message);
			return;
		} else {
			setSent(true);
			wrapperRef.current?.scrollIntoView({ behavior: "smooth" });
		}
	};

	return (
		<section
			className={clsx(styles["form-block"])}
			ref={wrapperRef}
		>
			<div className={clsx(styles["form-block__wrapper"], "grid-cols-12")}>
				{sent && (
					<div className={clsx(styles["form-block__success"], "col-full")}>
						<h2>Thank you</h2>
						<p>One of our team will be in touch as soon as possible.</p>
					</div>
				)}
				{!sent && (
					<div className={clsx(styles["form-block__inner"], "col-full col-md-8 col-lg-5")}>
						<FormProvider {...formMethods}>
							<form
								onSubmit={handleSubmit(onSubmit)}
								className={clsx(styles["form-block__form"], "col-full")}
							>
								{form?.fieldsets?.map((fieldset, index) => (
									<div key={fieldset._key}>
										<Fieldset
											className={styles["form-block__fieldset"]}
											legend={fieldset.heading || ""}
										>
											{fieldset.fields?.map(field => {
												const name = `${fieldset._key}:${field._key}`;
												if (field._type === "field")
													switch (field.fieldType) {
														case "text":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "textarea":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	rows={field.textareaRows}
																	type="textarea"
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "date":
															return (
																<DateField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		validate: {
																			isWeekend: field.disableWeekends
																				? date =>
																						date && isWeekend(date.toString())
																							? "Please select a weekday"
																							: true
																				: () => true,
																		},
																		pattern: {
																			value: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
																			message: "Please enter a valid date",
																		},
																	}}
																	isDateUnavailable={
																		field.disableWeekends
																			? date => isWeekend(date.toString())
																			: undefined
																	}
																/>
															);
														case "email":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		pattern: {
																			value: /\S+@\S+\.\S+/,
																			message: "Please enter a valid email address",
																		},
																	}}
																	placeholder={"example@domain.com"}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "phone":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	type="tel"
																	registerOptions={{
																		setValueAs: (value: string) => value.replaceAll(" ", ""),
																		required: requiredMessage(field.isMandatory),
																		pattern: {
																			value: /^((\+61|0)(2|3|4|7|8)\d{8})$/,
																			message: "Please enter a valid phone number",
																		},
																	}}
																	placeholder={"+61"}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "number":
															return (
																<InputField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		pattern: { value: /\d/, message: "Please enter a number" },
																	}}
																	placeholder={field.placeholderText}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "radio":
															return (
																<RadioGroupField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	options={field.radioItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "checkbox":
															return (
																<CheckboxGroupField
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	help={
																		field.isMandatory
																			? field.checkboxValidationStrategy === "all"
																				? "Please tick all checkboxes to proceed"
																				: "Please select at least one"
																			: undefined
																	}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																		validate: {
																			oneOrMany: (value: string[] | string) => {
																				const isMandatory = field.isMandatory;
																				const strategy = field.checkboxValidationStrategy || "one";
																				const count = Array.isArray(value)
																					? value.length
																					: value
																					? 1
																					: 0;
																				if (isMandatory) {
																					switch (strategy) {
																						case "one":
																							return count > 0 || "Please select at least one";
																						case "all":
																							return (
																								count === (field.checkboxItems?.length || 0) ||
																								"Please tick all checkboxes to proceed"
																							);
																					}
																				}
																				return true;
																			},
																		},
																	}}
																	options={field.checkboxItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);
														case "dropdown":
															return (
																<DropdownField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	placeholder={field.placeholderText}
																	name={name}
																	registerOptions={{
																		required: requiredMessage(field.isMandatory),
																	}}
																	options={field.dropdownItems?.map(item => ({
																		label: item,
																		value: item,
																	}))}
																	hideLabel={!field.showLabelText}
																/>
															);

														case "file":
															return (
																<FileUploadField
																	control={control}
																	key={field._key}
																	label={field.label || ""}
																	name={name}
																	registerOptions={{ required: requiredMessage(field.isMandatory) }}
																	hideLabel={!field.showLabelText}
																/>
															);
														default:
															return null;
													}

												if (field._type === "staticMessage" && field.message) {
													return (
														<div key={field._key}>
															<PortableTextRichText value={field.message} />
														</div>
													);
												}
											})}
										</Fieldset>
									</div>
								))}

								<Button
									className={styles["form-block__submit"]}
									type="submit"
									disabled={isSubmitting}
									label={isSubmitting ? `Submitting ...` : form?.submitButtonText}
								/>
							</form>
						</FormProvider>
					</div>
				)}
			</div>
		</section>
	);
};

const postForm = async (url: string, data: unknown) => {
	try {
		const response = await fetch(url, {
			method: "POST",
			body: data as BodyInit,
		});
		if (!response.ok) {
			const res = await response.json();
			throw new Error(res.message);
		}
		return { success: true };
	} catch (error) {
		if (error instanceof Error) return { success: false, message: error.message };
		return {
			success: false,
			message: "Mail not sent due to Unknown error, please contact site admin",
		};
	}
};
